import { Feather, Heart, MessageCircle, Repeat2, Send, Share, ThumbsUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type Platform = "x" | "linkedin" | "facebook";

interface SocialFeedMockupProps {
  platform: Platform;
  hostname: string;
  previewImageUrl: string | null;
  isLoading: boolean;
  logoUrl: string | null;
  bgColor: string;
  textColor: string;
  pageTitle: string | null;
  pageDescription: string | null;
  heroImageUrl: string | null;
  onPostClick: () => void;
}

export function SocialFeedMockup({
  platform,
  hostname,
  previewImageUrl,
  isLoading,
  logoUrl,
  bgColor,
  textColor,
  pageTitle,
  pageDescription,
  heroImageUrl,
  onPostClick,
}: SocialFeedMockupProps) {
  const displayName = hostname || "yourdomain.com";
  const handle = `@${displayName.replace(/\./g, "_")}`;
  const domainUrl = `https://${displayName}/`;
  const ogTitle = pageTitle ?? "";
  const ogDescription = pageDescription ?? "";

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {/* Compose box — "follow-up" post with Post CTA (above the fold) */}
      <ComposeBox hostname={displayName} isLoading={isLoading} onPostClick={onPostClick} />

      {/* Existing "launch" post in the feed */}
      <FeedPost
        platform={platform}
        displayName={displayName}
        handle={handle}
        domainUrl={domainUrl}
        previewImageUrl={previewImageUrl}
        isLoading={isLoading}
        hostname={hostname}
        logoUrl={logoUrl}
        bgColor={bgColor}
        textColor={textColor}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        heroImageUrl={heroImageUrl}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  OG Image — rendered client-side, updates live via props           */
/* ------------------------------------------------------------------ */

function OgImagePreview({
  logoUrl,
  ogTitle,
  ogDescription,
  heroImageUrl,
}: {
  logoUrl: string | null;
  ogTitle: string;
  ogDescription: string;
  heroImageUrl: string | null;
}) {
  const hasImage = Boolean(heroImageUrl);

  return (
    <div
      className={`relative z-10 grid h-full overflow-hidden ${hasImage ? "grid-cols-[minmax(0,1fr)_60%]" : "w-full"}`}
    >
      <div
        className={`flex min-w-0 flex-col overflow-hidden break-words ${
          hasImage ? "pt-4 pr-3 pb-24 pl-4" : "px-6 pt-5 pb-7"
        }`}
      >
        <div
          className={`${hasImage ? "flex h-full min-w-0 flex-col justify-around gap-4" : "flex flex-grow flex-col justify-between gap-8"}`}
        >
          {logoUrl && (
            <div className={hasImage ? "flex justify-start" : ""}>
              <img
                src={logoUrl}
                alt="Logo"
                className="max-w-24"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
          )}
          <div className={`flex min-w-0 flex-col gap-4 ${ogDescription ? "mb-24" : "mb-12"}`}>
            <h1 className="line-clamp-3 text-xl leading-tight font-semibold tracking-tight md:text-xl">{ogTitle}</h1>
            {ogDescription && <p className="text-xs leading-relaxed opacity-90">{ogDescription}</p>}
          </div>
        </div>
      </div>
      {heroImageUrl ? (
        <div className="relative h-full w-full overflow-hidden">
          <img src={heroImageUrl} alt="Hero" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Platform-specific OG link card (below the image)                  */
/* ------------------------------------------------------------------ */

function OgCardMeta({
  platform,
  hostname,
  ogTitle,
  ogDescription,
}: {
  platform: Platform;
  hostname: string;
  ogTitle: string;
  ogDescription: string;
}) {
  if (platform === "x") {
    return null;
  }

  if (platform === "linkedin") {
    return (
      <div className="border-t border-neutral-200 bg-white px-3 py-2.5 dark:border-neutral-700 dark:bg-neutral-800">
        <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{ogTitle}</p>
        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{hostname}</p>
      </div>
    );
  }

  return (
    <div className="border-t border-neutral-200 bg-white px-3 py-2.5 dark:border-neutral-700 dark:bg-neutral-800">
      <p className="truncate text-xs text-neutral-500 uppercase dark:text-neutral-400">{hostname}</p>
      <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{ogTitle}</p>
      <p className="line-clamp-1 text-xs text-neutral-500 dark:text-neutral-400">{ogDescription}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Feed post — the "launch announcement" post                       */
/* ------------------------------------------------------------------ */

function FeedPost({
  platform,
  displayName,
  handle,
  domainUrl,
  previewImageUrl,
  isLoading,
  hostname,
  logoUrl,
  bgColor,
  textColor,
  ogTitle,
  ogDescription,
  heroImageUrl,
}: {
  platform: Platform;
  displayName: string;
  handle: string;
  domainUrl: string;
  previewImageUrl: string | null;
  isLoading: boolean;
  hostname: string;
  logoUrl: string | null;
  bgColor: string;
  textColor: string;
  ogTitle: string;
  ogDescription: string;
  heroImageUrl: string | null;
}) {
  return (
    <div className="rounded-xl border border-neutral-100 bg-white p-3 sm:p-4 dark:border-neutral-800 dark:bg-neutral-800/30">
      {/* Post header */}
      <div className="mb-2 flex items-center gap-2 sm:mb-3 sm:gap-2.5">
        <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-neutral-200 sm:h-8 sm:w-8 dark:bg-neutral-700">
          <img
            src="https://api.dicebear.com/7.x/avataaars/svg?seed=JaneSmith"
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex-1">
          {platform === "x" ? (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-neutral-900 dark:text-white">{displayName}</span>
              <span className="text-xs text-neutral-400">{handle}</span>
              <span className="text-xs text-neutral-400">· 5m</span>
            </div>
          ) : (
            <div>
              <span className="text-sm font-semibold text-neutral-900 dark:text-white">{displayName}</span>
              <div className="text-[10px] text-neutral-400">5 min ago</div>
            </div>
          )}
        </div>
      </div>

      {/* Launch announcement post text */}
      <p className="mb-2 text-xs whitespace-pre-line text-neutral-700 sm:mb-3 sm:text-sm dark:text-neutral-300">
        {platform === "x" ? (
          <>
            We just upgraded our social previews with OG Pilot! Every link now gets a branded OG image automatically 🚀
          </>
        ) : (
          <>
            Excited to announce we&apos;ve partnered with OG Pilot to auto-generate branded social previews for every
            page on <span className="text-blue-500">{domainUrl}</span>.{`\n\n`}No more generic link cards. Every share
            now looks on-brand 🎨
          </>
        )}
      </p>

      {/* OG image + platform-specific link card */}
      <div
        className={`w-full overflow-hidden border border-neutral-200 dark:border-neutral-700 ${
          platform === "x" ? "rounded-2xl" : "rounded-lg"
        }`}
      >
        <div className="relative aspect-[1200/630] w-full" style={{ backgroundColor: bgColor, color: textColor }}>
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : previewImageUrl ? (
            <img src={previewImageUrl} alt="OG image preview" className="h-full w-full object-cover" />
          ) : (
            <OgImagePreview
              logoUrl={logoUrl}
              ogTitle={ogTitle}
              ogDescription={ogDescription}
              heroImageUrl={heroImageUrl}
            />
          )}

          {/* X: title overlay badge at bottom-left */}
          {platform === "x" && !isLoading && (
            <div className="pointer-events-none absolute bottom-0 left-0 m-3 max-w-[80%] truncate rounded-sm bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
              {ogTitle}
            </div>
          )}
        </div>

        {/* Facebook / LinkedIn: metadata card below the image */}
        {platform !== "x" && !isLoading && (
          <OgCardMeta platform={platform} hostname={hostname} ogTitle={ogTitle} ogDescription={ogDescription} />
        )}
      </div>

      {/* Engagement bar */}
      <div className="mt-3 flex items-center justify-between text-neutral-400">
        {platform === "x" ? (
          <>
            <EngagementButton icon={MessageCircle} count="4" />
            <EngagementButton icon={Repeat2} count="12" />
            <EngagementButton icon={Heart} count="38" />
            <EngagementButton icon={Share} />
          </>
        ) : platform === "linkedin" ? (
          <>
            <EngagementButton icon={ThumbsUp} label="Like" />
            <EngagementButton icon={MessageCircle} label="Comment" />
            <EngagementButton icon={Repeat2} label="Repost" />
            <EngagementButton icon={Send} label="Send" />
          </>
        ) : (
          <>
            <EngagementButton icon={ThumbsUp} label="Like" />
            <EngagementButton icon={MessageCircle} label="Comment" />
            <EngagementButton icon={Share} label="Share" />
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Compose box — the "follow-up" post with prominent Post CTA        */
/* ------------------------------------------------------------------ */

function ComposeBox({
  hostname,
  isLoading,
  onPostClick,
}: {
  hostname: string;
  isLoading: boolean;
  onPostClick: () => void;
}) {
  return (
    <div className="rounded-xl border-2 border-orange-200 bg-orange-50/30 p-3 sm:p-4 dark:border-orange-500/30 dark:bg-orange-500/5">
      <div className="flex items-start gap-2.5 sm:gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-200 sm:h-9 sm:w-9 dark:bg-neutral-700">
          <img
            src="https://api.dicebear.com/7.x/avataaars/svg?seed=JaneSmith"
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex-1">
          {isLoading ? (
            <Skeleton className="h-12 w-full rounded-lg" />
          ) : (
            <div className="rounded-lg border border-neutral-200 bg-white px-2.5 py-2 text-xs sm:px-3 sm:text-sm dark:border-neutral-700 dark:bg-neutral-800">
              <span className="text-neutral-600 dark:text-neutral-400">
                Loving how <span className="text-blue-500">{hostname}</span> looks when shared now — branded previews
                everywhere ✨
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2 sm:mt-3">
        <p className="text-[10px] leading-tight text-orange-600/80 sm:text-[11px] dark:text-orange-400/80">
          Happy with the preview? Post to get your API key.
        </p>
        <Button
          size="sm"
          onClick={onPostClick}
          disabled={isLoading}
          className="gap-1.5 rounded-full bg-orange-500 text-white shadow-md shadow-orange-200 hover:bg-orange-600 dark:shadow-orange-500/20"
        >
          <Feather className="h-3.5 w-3.5" />
          Post
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Engagement button                                                  */
/* ------------------------------------------------------------------ */

function EngagementButton({
  icon: Icon,
  count,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  count?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      className="flex items-center gap-1 rounded-full px-2 py-1 text-[11px] text-neutral-400 transition-colors hover:text-neutral-600 dark:hover:text-neutral-300"
    >
      <Icon className="h-4 w-4" />
      {count && <span>{count}</span>}
      {label && <span>{label}</span>}
    </button>
  );
}
